import { useEffect, useState, type FormEvent } from "react";
import { Navigate, useNavigate } from "react-router";
import { DISTRICTS } from "../../shared/districts.ts";
import { formatRelative } from "../../shared/i18n/index.ts";
import { formatPhone, normalizePhone } from "../../shared/phone.ts";
import type { Me } from "../../shared/types.ts";
import { ApiError, api } from "../lib/api.ts";
import { serverNow } from "../lib/clock.ts";
import { useI18n } from "../lib/i18n.tsx";
import { INVITE_STORAGE_KEY, storedInvite, useMe } from "../lib/me.tsx";
import { useMeta } from "../lib/meta.ts";

type SessionInfo = {
  id: string;
  ua: string;
  created_at: number;
  last_seen_at: number;
  current: boolean;
};
type InviteInfo = {
  code: string;
  used: boolean;
  used_at: number | null;
  used_by_handle: string | null;
};
type InviteList = { codes: InviteInfo[]; unused: number };

function clearStoredInvite() {
  try {
    localStorage.removeItem(INVITE_STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

function describeUa(ua: string): string {
  if (/iPhone|iPad/.test(ua)) return "iPhone";
  if (/Android/.test(ua)) return "Android";
  if (/Macintosh/.test(ua)) return "Mac";
  if (/Windows/.test(ua)) return "Windows";
  if (/Linux/.test(ua)) return "Linux";
  return ua.slice(0, 24) || "?";
}

export function Settings() {
  const { t, locale, setLocale } = useI18n();
  const { me, loading, setMe, logout } = useMe();
  if (loading) return <div className="page muted">{t("common.loading")}</div>;
  if (!me) return <Navigate to="/login?next=/me/settings" replace />;
  return (
    <SettingsForm
      me={me}
      setMe={setMe}
      logout={logout}
      t={t}
      locale={locale}
      setLocale={setLocale}
    />
  );
}

type Props = {
  me: Me;
  setMe: (m: Me | null) => void;
  logout: () => Promise<void>;
  t: ReturnType<typeof useI18n>["t"];
  locale: "vi" | "en";
  setLocale: (l: "vi" | "en") => void;
};

function SettingsForm({ me, setMe, t, locale, setLocale }: Props) {
  const navigate = useNavigate();
  const meta = useMeta();
  const [flash, setFlash] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // profile
  const [displayName, setDisplayName] = useState(me.display_name);
  const [cityId, setCityId] = useState<number | "">(me.city_id ?? "");
  const [district, setDistrict] = useState(me.district ?? "");
  const [email, setEmail] = useState(me.email ?? "");
  const [handle, setHandle] = useState(me.handle);
  // phone
  const [newPhone, setNewPhone] = useState("");
  const [phoneCode, setPhoneCode] = useState("");
  const [phoneStep, setPhoneStep] = useState<"idle" | "code">("idle");
  // devices
  const [sessions, setSessions] = useState<SessionInfo[] | null>(null);
  // delete
  const [confirmDelete, setConfirmDelete] = useState(false);
  // invites
  const [inviteCode, setInviteCode] = useState(() => storedInvite() ?? "");
  const [invites, setInvites] = useState<InviteList | null>(null);
  const [inviteFlash, setInviteFlash] = useState<string | null>(null);
  const [copied, setCopied] = useState<string | null>(null);
  const [tgUrl, setTgUrl] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    api<SessionInfo[]>("/api/me/sessions")
      .then((s) => live && setSessions(s))
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, []);

  useEffect(() => {
    if (!me.can_sell) return;
    let live = true;
    api<InviteList>("/api/me/invites")
      .then((i) => live && setInvites(i))
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, [me.can_sell]);

  const citySlug = meta?.cities.find((c) => c.id === cityId)?.slug;
  const districts = citySlug ? (DISTRICTS[citySlug] ?? []) : [];
  const handleCooldownUntil = me.handle_changed_at ? me.handle_changed_at + 90 * 86_400 : null;
  const handleLocked = handleCooldownUntil !== null && handleCooldownUntil > serverNow();

  function fail(err: unknown) {
    if (err instanceof ApiError) {
      const map: Record<string, Parameters<typeof t>[0]> = {
        handle_taken: "error.handle_taken",
        handle_cooldown: "error.handle_cooldown",
        phone_taken: "error.handle_taken",
        invalid_code: "error.invalid_code",
        code_expired: "error.code_expired",
        rate_limited: "error.rate_limited",
        cooldown: "error.rate_limited",
        validation: "error.validation",
      };
      setError(t(map[err.body.error] ?? "error.generic"));
    } else setError(t("error.network"));
  }

  async function saveProfile(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setFlash(null);
    try {
      const updated = await api<Me>("/api/me/profile", {
        method: "PUT",
        body: {
          display_name: displayName,
          city_id: cityId === "" ? null : cityId,
          district: district || null,
          email: email || null,
          locale,
        },
      });
      setMe(updated);
      setFlash(t("settings.saved"));
    } catch (err) {
      fail(err);
    }
  }

  async function saveHandle(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setFlash(null);
    try {
      setMe(await api<Me>("/api/me/handle", { method: "PUT", body: { handle } }));
      setFlash(t("settings.saved"));
    } catch (err) {
      fail(err);
    }
  }

  async function sendPhoneCode(e: FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      await api("/api/me/phone/otp", { body: { phone: newPhone } });
      setPhoneStep("code");
    } catch (err) {
      fail(err);
    }
  }

  async function verifyPhone(value: string) {
    setError(null);
    try {
      setMe(await api<Me>("/api/me/phone/verify", { body: { phone: newPhone, code: value } }));
      setPhoneStep("idle");
      setNewPhone("");
      setPhoneCode("");
      setFlash(t("settings.saved"));
    } catch (err) {
      setPhoneCode("");
      fail(err);
    }
  }

  async function revoke(id: string) {
    await api(`/api/me/sessions/${id}`, { method: "DELETE" }).catch(() => undefined);
    setSessions((s) => s?.filter((x) => x.id !== id) ?? null);
  }

  async function revokeOthers() {
    await api("/api/me/sessions/revoke-others", { body: {} }).catch(() => undefined);
    setSessions((s) => s?.filter((x) => x.current) ?? null);
  }

  async function redeem(e?: FormEvent) {
    e?.preventDefault();
    setError(null);
    try {
      const updated = await api<Me>("/api/invites/redeem", { body: { code: inviteCode } });
      clearStoredInvite();
      setInviteCode("");
      setMe(updated);
      setInviteFlash(t("settings.invite.redeemed"));
    } catch (err) {
      if (err instanceof ApiError && err.body.error === "invalid_invite")
        setError(t("error.invalid_invite"));
      else fail(err);
    }
  }

  async function copyLink(code: string) {
    const url = `${window.location.origin}/i/${code}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(code);
      setTimeout(() => setCopied(null), 1500);
    } catch {
      window.prompt(t("common.copy"), url);
    }
  }

  async function linkTelegram() {
    setError(null);
    try {
      const r = await api<{ url: string }>("/api/me/telegram/link", { body: {} });
      setTgUrl(r.url);
      window.open(r.url, "_blank", "noopener");
    } catch (err) {
      fail(err);
    }
  }

  async function unlinkTelegram() {
    await api("/api/me/telegram", { method: "DELETE" }).catch(() => undefined);
    setMe({ ...me, telegram_linked: false });
    setTgUrl(null);
  }

  async function deleteAccount() {
    try {
      await api("/api/me/delete", { body: { confirm: true } });
      setMe(null);
      navigate("/", { replace: true });
    } catch (err) {
      fail(err);
    }
  }

  const now = serverNow();
  return (
    <div className="page page--narrow">
      <h1 className="h1">{t("settings.title")}</h1>
      {flash ? (
        <p className="ok" role="status">
          {flash}
        </p>
      ) : null}
      {error ? (
        <p className="error" role="alert">
          {error}
        </p>
      ) : null}

      <section className="section">
        <h2 className="h2">{t("settings.profile")}</h2>
        <form className="form" onSubmit={saveProfile}>
          <label className="field">
            <span className="label">{t("auth.onboard.display_name")}</span>
            <input
              className="input"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              minLength={2}
              maxLength={30}
              required
              data-testid="settings-name"
            />
          </label>
          <label className="field">
            <span className="label">{t("settings.city")}</span>
            <select
              className="input"
              value={cityId}
              onChange={(e) => {
                setCityId(e.target.value ? Number(e.target.value) : "");
                setDistrict("");
              }}
            >
              <option value="">—</option>
              {meta?.cities.map((c) => (
                <option key={c.id} value={c.id}>
                  {locale === "vi" ? c.name_vi : c.name_en}
                </option>
              ))}
            </select>
          </label>
          <label className="field">
            <span className="label">{t("settings.district")}</span>
            <input
              className="input"
              list="districts"
              value={district}
              onChange={(e) => setDistrict(e.target.value)}
              maxLength={60}
            />
            <datalist id="districts">
              {districts.map((d) => (
                <option key={d} value={d} />
              ))}
            </datalist>
          </label>
          <label className="field">
            <span className="label">{t("common.language")}</span>
            <select
              className="input"
              value={locale}
              onChange={(e) => setLocale(e.target.value as "vi" | "en")}
            >
              <option value="vi">Tiếng Việt</option>
              <option value="en">English</option>
            </select>
          </label>
          <label className="field">
            <span className="label">{t("settings.email")}</span>
            <input
              className="input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </label>
          <button className="btn btn--accent" type="submit" data-testid="settings-save">
            {t("common.save")}
          </button>
        </form>
      </section>

      <section className="section">
        <h2 className="h2">{t("auth.onboard.handle")}</h2>
        <form className="form" onSubmit={saveHandle}>
          <label className="field">
            <input
              className="input"
              value={handle}
              onChange={(e) =>
                setHandle(
                  e.target.value
                    .toLowerCase()
                    .replace(/[^a-z0-9_]/g, "")
                    .slice(0, 20),
                )
              }
              pattern="[a-z0-9_]{3,20}"
              disabled={handleLocked}
            />
            <span className="hint">
              {handleLocked && handleCooldownUntil
                ? t("settings.handle.cooldown", {
                    date: new Date(handleCooldownUntil * 1000).toLocaleDateString("vi-VN"),
                  })
                : t("error.handle_cooldown")}
            </span>
          </label>
          <button className="btn" type="submit" disabled={handleLocked || handle === me.handle}>
            {t("common.save")}
          </button>
        </form>
      </section>

      <section className="section">
        <h2 className="h2">{t("settings.phone")}</h2>
        <p className="muted">
          {t("settings.phone.current")}: {me.phone ? formatPhone(me.phone) : "—"}
        </p>
        {phoneStep === "idle" ? (
          <form className="form" onSubmit={sendPhoneCode}>
            <label className="field">
              <span className="label">{t("settings.phone.new")}</span>
              <input
                className="input"
                type="tel"
                inputMode="tel"
                value={newPhone}
                onChange={(e) => setNewPhone(e.target.value)}
                placeholder="0901 234 567"
              />
            </label>
            <button className="btn" type="submit" disabled={!normalizePhone(newPhone)}>
              {t("auth.phone.submit")}
            </button>
          </form>
        ) : (
          <form
            className="form"
            onSubmit={(e) => {
              e.preventDefault();
              if (phoneCode.length === 6) void verifyPhone(phoneCode);
            }}
          >
            <p className="muted">
              {t("auth.otp.sent_to", { phone: formatPhone(normalizePhone(newPhone) ?? newPhone) })}
            </p>
            <input
              className="input input--otp"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              value={phoneCode}
              onChange={(e) => {
                const v = e.target.value.replace(/\D/g, "").slice(0, 6);
                setPhoneCode(v);
                if (v.length === 6) void verifyPhone(v);
              }}
            />
            <button className="btn btn--ghost" type="button" onClick={() => setPhoneStep("idle")}>
              {t("common.cancel")}
            </button>
          </form>
        )}
      </section>

      <section className="section">
        <h2 className="h2">{t("settings.notifications")}</h2>
        {me.telegram_linked ? (
          <div className="row row--between">
            <span className="ok">{t("settings.telegram.linked")} ✓</span>
            <button className="btn btn--ghost" onClick={() => void unlinkTelegram()}>
              {t("settings.telegram.unlink")}
            </button>
          </div>
        ) : (
          <div className="stack">
            <button className="btn" onClick={() => void linkTelegram()} data-testid="tg-link">
              {t("settings.telegram.link")}
            </button>
            {tgUrl ? (
              <a
                className="hint"
                href={tgUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="tg-url"
              >
                {tgUrl}
              </a>
            ) : null}
          </div>
        )}
      </section>

      <section className="section">
        <label className="check">
          <input
            type="checkbox"
            checked={me.drop_reminders}
            onChange={(e) =>
              void api<Me>("/api/me/notification-settings", {
                method: "PUT",
                body: { drop_reminders: e.target.checked },
              })
                .then(setMe)
                .catch(() => undefined)
            }
            data-testid="drop-reminders"
          />
          <span>{t("settings.drop_reminders")}</span>
        </label>
        {me.can_sell ? (
          <label className="check" style={{ marginTop: 12 }}>
            <input
              type="checkbox"
              checked={me.auto_share_contact}
              onChange={(e) =>
                void api("/api/me/auto-share", {
                  method: "PUT",
                  body: { enabled: e.target.checked },
                })
                  .then(() => setMe({ ...me, auto_share_contact: e.target.checked }))
                  .catch(() => undefined)
              }
              data-testid="auto-share"
            />
            <span>
              {t("settings.auto_share")}
              <span className="hint">{t("settings.auto_share.hint")}</span>
            </span>
          </label>
        ) : null}
      </section>

      <section className="section">
        <h2 className="h2">{t("settings.become_seller")}</h2>
        {inviteFlash ? (
          <p className="ok" role="status">
            {inviteFlash}
          </p>
        ) : null}
        {!me.can_sell ? (
          <form className="form" onSubmit={redeem}>
            <label className="field">
              <span className="label">{t("settings.invite.enter")}</span>
              <input
                className="input input--code"
                value={inviteCode}
                onChange={(e) =>
                  setInviteCode(
                    e.target.value
                      .toUpperCase()
                      .replace(/[^A-Z0-9]/g, "")
                      .slice(0, 8),
                  )
                }
                maxLength={8}
                data-testid="invite-code"
              />
            </label>
            <button
              className="btn btn--accent"
              type="submit"
              disabled={inviteCode.length !== 8}
              data-testid="invite-redeem"
            >
              {t("settings.invite.redeem")}
            </button>
            <p className="muted small">{t("sell.need_invite")}</p>
          </form>
        ) : (
          <div className="stack">
            <p data-testid="seller-status">
              {t("settings.seller.status", { n: invites?.unused ?? 0 })}
            </p>
            <p className="muted small">{t("settings.invite.link")}</p>
            <ul className="list" data-testid="invite-codes">
              {invites?.codes.map((i) => (
                <li key={i.code} className="list__row">
                  <div>
                    <code className={i.used ? "code code--used" : "code"}>{i.code}</code>
                    {i.used ? (
                      <div className="hint">
                        {t("settings.invite.used_by", { handle: i.used_by_handle ?? "?" })}
                      </div>
                    ) : null}
                  </div>
                  {!i.used ? (
                    <button
                      className="btn btn--ghost"
                      type="button"
                      onClick={() => void copyLink(i.code)}
                    >
                      {copied === i.code ? t("common.copied") : t("common.copy")}
                    </button>
                  ) : null}
                </li>
              ))}
            </ul>
            <p className="muted small">{t("settings.invite.topup")}</p>
          </div>
        )}
      </section>

      <section className="section">
        <h2 className="h2">{t("settings.devices")}</h2>
        <ul className="list" data-testid="devices">
          {sessions?.map((s) => (
            <li key={s.id} className="list__row">
              <div>
                <div>
                  {describeUa(s.ua)}
                  {s.current ? ` · ${t("settings.devices.this")}` : ""}
                </div>
                <div className="hint">
                  {t("settings.devices.last_seen", {
                    when: formatRelative(s.last_seen_at, now, locale),
                  })}
                </div>
              </div>
              {!s.current ? (
                <button className="btn btn--ghost" onClick={() => void revoke(s.id)}>
                  {t("settings.devices.revoke")}
                </button>
              ) : null}
            </li>
          ))}
        </ul>
        {sessions && sessions.length > 1 ? (
          <button className="btn" onClick={() => void revokeOthers()} data-testid="revoke-others">
            {t("settings.devices.revoke_all")}
          </button>
        ) : null}
      </section>

      <section className="section">
        <h2 className="h2">{t("settings.delete")}</h2>
        <p className="muted small">{t("settings.delete.warning")}</p>
        <p className="muted small">{t("settings.export.hint")}</p>
        {!confirmDelete ? (
          <button
            className="btn btn--danger"
            onClick={() => setConfirmDelete(true)}
            data-testid="delete-open"
          >
            {t("settings.delete")}
          </button>
        ) : (
          <div className="sheet">
            <strong>{t("settings.delete.confirm_title")}</strong>
            <div className="row">
              <button
                className="btn btn--danger"
                onClick={() => void deleteAccount()}
                data-testid="delete-confirm"
              >
                {t("settings.delete.confirm")}
              </button>
              <button className="btn btn--ghost" onClick={() => setConfirmDelete(false)}>
                {t("common.cancel")}
              </button>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}
